/**
 * Which connections a tab needs.
 *
 * Most tabs answer this with `connectionInfo.connectionId`, but two kinds do
 * not, and reading only `connectionInfo` for them is why the app could promise
 * one connection and open two:
 *
 * - **Schema Compare / Data Compare** carry BOTH endpoints in `content`. The
 *   tab is unusable until both sides are open, so both are "needed".
 * - **Database Blueprint** carries no connection at all — its connection is the
 *   active link recorded on the mounted blueprint folder.
 *
 * Mirrors `useActiveTabConnectionNeed` (the selector that decides whether the
 * amber connection banner appears, `Docs/ConnectionBanner.md`) so "what does
 * this tab need" has one answer everywhere: source endpoint first for compare
 * tabs, the resolved active link for blueprints.
 *
 * Deliberately shaped to accept BOTH a `SerializedTab` and a live `Tab` — the
 * session-side callers (eager-connect resolution, pruning, exclusion) work on
 * whichever is at hand.
 */
import { useScriptBrowserStore } from '../../script-browser/store/scriptBrowserStore';
import { resolveActiveLinkLabel } from '../../database-blueprint/utils/activeLink';

/** The minimum shape a serialized tab and a live tab share. */
export type ConnectionBearingTab = {
  type: string;
  connectionInfo?: { connectionId?: string } | undefined;
  content?: unknown;
};

/** Endpoints of a compare tab, as they survive serialization. */
type CompareEndpoint = { kind?: string; connectionId?: string };

function compareEndpointIds(content: unknown): string[] {
  // The content union isn't keyed on `type`, and legacy data-compare endpoints
  // predate the `kind` discriminator (absent === 'connection').
  const dual = (content ?? {}) as { source?: CompareEndpoint; target?: CompareEndpoint };
  return [dual.source, dual.target]
    .filter((e) => e && e.kind !== 'blueprint-folder')
    .map((e) => e?.connectionId)
    .filter((id): id is string => !!id);
}

/**
 * The connection behind a Database Blueprint tab: its active link, read off the
 * mounted folder the tab points at. A non-reactive read of the same
 * script-browser state `useActiveTabConnectionNeed` subscribes to — the store
 * is localStorage-persisted and hydrates synchronously, so it is already
 * populated when a session restore asks.
 */
function blueprintConnectionIds(content: unknown): string[] {
  const blueprint = (content ?? {}) as {
    blueprintFolderId?: string;
    selectedLinkedDbLabel?: string;
  };
  if (!blueprint.blueprintFolderId) return [];

  const state = useScriptBrowserStore.getState();
  const workspace = state.activeWorkspaceId
    ? state.workspaces?.find((w) => w.id === state.activeWorkspaceId)
    : null;
  const mountedFolders = workspace
    ? workspace.mountedFolders
    : state.defaultWorkspaceState.mountedFolders;

  const folder = mountedFolders.find(
    (f) => f.id === blueprint.blueprintFolderId && f.kind === 'blueprint' && f.blueprintMetadata
  );
  const linkedDbs = folder?.blueprintMetadata?.linkedDbs ?? [];
  const label = resolveActiveLinkLabel({
    activeLinkLabel: blueprint.selectedLinkedDbLabel ?? folder?.blueprintMetadata?.activeLinkLabel,
    linkedDbs,
  });
  const connectionId = linkedDbs.find((l) => l.label === label)?.connectionId;
  return connectionId ? [connectionId] : [];
}

/**
 * Connection ids the tab needs, in the order the workspace resolves them
 * (source endpoint first for compare tabs). Never contains duplicates.
 */
export function tabConnectionIds(tab: ConnectionBearingTab): string[] {
  const ids =
    tab.type === 'schema-compare' || tab.type === 'data-compare'
      ? compareEndpointIds(tab.content)
      : tab.type === 'database-blueprint'
        ? blueprintConnectionIds(tab.content)
        : tab.connectionInfo?.connectionId
          ? [tab.connectionInfo.connectionId]
          : [];

  return Array.from(new Set(ids));
}
